import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import generateUuid from 'uuid/v4'
import { isEmpty, reduce, values, map, head, find, isEqual } from 'lodash'

import useDeepCompareEffect from 'use-deep-compare-effect'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import List from '../List'
import SidebarModal from '../SidebarModal'
import TabOrderTotals from './TabOrderTotals'
import Button from '../Button'
import CardReader from '../CardReader'
import BackChevron from '../BackChevron'
import RadioSelectionModal from '../RadioSelectionModal'
import { ReactComponent as Checkmark } from '../../assets/icons/checkmark-icon.svg'

import { makeDefaultExemptions, makeDiscounts, makeSelectedExemptions, calculateTaxAndTotal } from '../../utils/exemptionUtils'
import useManagerApproval from '../../hooks/useManagerApproval'

import styles from './TabOrderPayNowModal.module.scss'
import { centsToDollars } from '../../utils/formatters'
import { USE_RICH_CHECKOUT, SIGNATURE_THRESHOLD_AMOUNT } from '../../constants'
import VNNotesField from '../VNNotesField'
import { nextPosOrderNumber } from '../../utils/orderNumbers'
import fakeCreditCardResponse from '../../utils/fakeCreditCardResponse'

const requiresApproval = (item, index) => {
  const isApplyDiscounts = index === 0 && item.selected === false
  const isSalesTaxOrServiceFee = index > 0 && item.selected === true

  if (isApplyDiscounts || isSalesTaxOrServiceFee) {
    return true
  } else {
    return false
  }
}

const getDefaultSelectedDiscountIndexes = (discounts) => {
  return reduce(discounts, (acc, discount, index) => {
    if (discount.selected) acc.push(index)
    return acc
  }, [])
}

const isRichCheckoutEnabled = USE_RICH_CHECKOUT === 'true'

const TabOrderPayNowModal = ({
  alternatePaymentPreference,
  cashEnabled,
  className,
  deviceReceiptPrinterUuid,
  discountsAvailable,
  exemptionOverrides,
  displayName,
  fetchPrinters,
  finalOrderState,
  frozen,
  getRemoteOrderTotal,
  groupedItems,
  items,
  itemCount,
  isNewOrder,
  isNoTipMenu,
  isRemotePayment,
  isSuitesMode,
  noOpPayments,
  onCashPayment,
  onClose,
  onCloseWithNewCard,
  onCloseWithStoredCard,
  onDone,
  onPlaceFreeOrder,
  onRemotePayment,
  onSelectNoOpPayment,
  onSuccess,
  onViewCheck,
  orderId,
  orderingPreference,
  payment,
  printOrder,
  printers,
  peripheralStatus,
  remoteOrderTotal,
  subtotal,
  total,
  paymentOptions,
  paymentOnOrder,
  pendingMutation,
  unsyncedLocalIds,
  history,
  ...props
}) => {
  const classNames = cn(styles.tabOrderPayNowModal, className)
  const [uuid] = useState(isEmpty(orderId) ? generateUuid() : orderId)
  const [isCardPayment, setIsCardPayment] = useState(false)
  const [approvalModal, requestApproval] = useManagerApproval()
  const [cardPaymentCompleted, setCardPaymentCompleted] = useState(false)
  const [exemptions, setExemptions] = useState(exemptionOverrides ?? makeDefaultExemptions(remoteOrderTotal, orderingPreference))
  const [discounts, setDiscounts] = useState(makeDiscounts(remoteOrderTotal, orderingPreference, exemptions))
  const [defaultSelectedDiscountIndexes, setDefaultSelectedDiscountIndexes] = useState(getDefaultSelectedDiscountIndexes(discounts))
  const [placingFreeOrder, setPlacingFreeOrder] = useState(false)
  
  const taxAndTotal = calculateTaxAndTotal(remoteOrderTotal, exemptions)
  const [grandTotal, setGrandTotal] = useState(taxAndTotal.total)
  const [tax, setTax] = useState(taxAndTotal.tax)
  
  const [selectPaymentFlag, setSelectPaymentFlag] = useState(false)
  const [nonCashPaymentSelected, setNonCashPaymentSelected] = useState(false)
  const [showPrinterSelection, setShowPrinterSelection] = useState(false)
  const notesRef = useRef()
  
  const unsyncedOrder = find(unsyncedLocalIds, (id) => id === uuid)
  const updateExemptions = !isEqual(exemptionOverrides, exemptions)
  const signatureRequired = SIGNATURE_THRESHOLD_AMOUNT === 0 || (SIGNATURE_THRESHOLD_AMOUNT && remoteOrderTotal.totalAmountInCents >= SIGNATURE_THRESHOLD_AMOUNT)
  const loading = remoteOrderTotal?.loading || remoteOrderTotal?.failed || !(pendingMutation === undefined) || placingFreeOrder
  
  const handleOnCardPayment = () => {
    setIsCardPayment(true)
  }
  
  useEffect(() => {
    getRemoteOrderTotal()
    fetchPrinters()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!loading && remoteOrderTotal?.totalAmountInCents === 0) {
      onPlaceFreeOrder()
      setPlacingFreeOrder(true)
    }
  }, [loading])

  const handleOnSuccess = (data) => {
    if (!isNewOrder && isCardPayment) {
      onCloseWithNewCard(data, exemptions, updateExemptions)
    } else {
      onSuccess(data, notesRef.current?.getNotes(), exemptions, signatureRequired, isNoTipMenu)
    }
    setCardPaymentCompleted(true)
    setNonCashPaymentSelected(true)
  }

  const updateExemptionsAndTotals = (newExemptions) => {
    setExemptions(newExemptions)

    const newDiscounts = makeDiscounts(remoteOrderTotal, orderingPreference, newExemptions)
    setDiscounts(newDiscounts)

    const defaultSelections = getDefaultSelectedDiscountIndexes(newDiscounts)
    setDefaultSelectedDiscountIndexes(defaultSelections)

    const taxAndTotal = calculateTaxAndTotal(remoteOrderTotal, newExemptions)
    setTax(taxAndTotal.tax)
    setGrandTotal(taxAndTotal.total)
  }

  const handleDiscountsDidChange = (selectedIndexes) => {
    const newExemptions = makeSelectedExemptions(selectedIndexes)
    updateExemptionsAndTotals(newExemptions)
  }

  const ExemptableItem = ({ loading, label, selected, onChange, disabled }) => {
    let checkmarkElement = null

    if (selected) {
      checkmarkElement = <Checkmark className={styles.discountCheckmarkIcon} />
    }

    const _onClick = () => {
      if (!disabled) {
        onChange()
      }
    }

    const setStyles = () => {
      if (disabled) {
        return cn(styles.labelContainer, styles.labelContainerDisabled)
      }
      return styles.labelContainer
    }

    let content = (
      <div className={setStyles()} htmlFor={label} onClick={_onClick}>
        <input disabled={disabled} id={label} type="checkbox" checked={selected} onChange={isNewOrder ? () => {} : onChange} />
        <span className={styles.discountCheckmark}>{checkmarkElement}</span>
        {label}
      </div>
    )

    if (loading) {
      content = (
        <SkeletonTheme color="#3c3e52" highlightColor="#52556f">
          <Skeleton height={22} width="75%"  />
        </SkeletonTheme>
      )
    }

    // FIXME: onChange does not work for new orders for some reason.
    return (
      <li className={styles.discount}>
        {content}
      </li>
    )
  }

  let backButton = null

  const makePaymentButton = (paymentPreference, index, collection) => {
    const classNames =
      !!collection
        ? styles.actionButton
        : [styles.actionButton, styles.mintGreenHighlight]

    return (
      <Button
        disabled={loading}
        key={paymentPreference?.externalRefId}
        onClick={() => {
          onRemotePayment(paymentPreference, exemptions, uuid, updateExemptions)
          setNonCashPaymentSelected(true)
        }}
        className={classNames}
      >
        {paymentPreference?.shortDescription}
      </Button>
    )
  }

  let savedPayments
  if (!isEmpty(paymentOptions) && !selectPaymentFlag && !(paymentOptions?.length === 1 && !isEmpty(paymentOnOrder))) {
    if (paymentOptions?.length > 1 || (isEmpty(paymentOnOrder) && !isNewOrder)) {
      savedPayments = (
        <Button disabled={loading} key='selectPayment' onClick={() => setSelectPaymentFlag(true)} className={styles.actionButton}>
          {"Saved Payments"}
        </Button>
      )
    } else {
      savedPayments = makePaymentButton(head(paymentOptions))
    }
  }

  let buttons = [
    discountsAvailable && <Button disabled={loading} key='discounts' onClick={() => history.push('/tab-order/pay-now/select-discount')} className={styles.actionButton}>Discounts</Button>,
    cashEnabled && <Button disabled={loading} key='cash' onClick={() => onCashPayment(exemptions, updateExemptions, notesRef.current?.getNotes())} className={styles.actionButton}>Cash</Button>,
    isRichCheckoutEnabled ? <Button key='mobile-wallet' onClick={() => history.push('/rich-checkout', { userNotes: notesRef.current?.getNotes() })} className={styles.actionButton} disabled={loading}>App</Button> : null,
    <Button disabled={loading} key='credit' onClick={handleOnCardPayment} className={styles.actionButton}>Card Reader</Button>,
    !isEmpty(noOpPayments) && <Button disabled={loading} key='no-op' onClick={() => onSelectNoOpPayment(exemptions, notesRef.current?.getNotes())} className={styles.actionButton}>Other</Button>,
    savedPayments,
  ]

  // TODO: (JEFF) TabOrderPayNow is always a new order, should remove all logic pertaining to !isNewOrder
  if (!isNewOrder) {
    buttons = [
      <Button disabled={loading} key='cash' onClick={() => onCashPayment(exemptions, updateExemptions, notesRef.current?.getNotes())} className={styles.actionButton}>Cash</Button>,
      <Button disabled={loading} key='newCard' onClick={() => setIsCardPayment(true)} className={styles.actionButton}>New Card</Button>,
      savedPayments,
      isEmpty(paymentOnOrder) && (payment !== 'Cash' && payment !== 'Payment Pending') && <Button disabled={loading} key='payment' onClick={() => { onCloseWithStoredCard(exemptions, updateExemptions); setNonCashPaymentSelected(true) }} className={[styles.actionButton, styles.mintGreenHighlight]}>{payment}</Button>,
      !isEmpty(paymentOnOrder) && makePaymentButton(paymentOnOrder)
    ]
  }

  if (selectPaymentFlag) {
    buttons = map(paymentOptions, makePaymentButton)
    if (isEmpty(paymentOnOrder) && payment !== 'Cash' && !isNewOrder) {
      buttons = [
        ...buttons,
        <Button key='payment' onClick={() => { onCloseWithStoredCard(exemptions, updateExemptions); setNonCashPaymentSelected(true) }} className={styles.actionButton}>{payment}</Button>,
      ]
    }
  }

  if (!isNewOrder && isCardPayment) {
    backButton = <BackChevron onClick={() => setIsCardPayment(false)} />
  }

  let footer = (
    <div className={styles.buttonContainer}>
      {buttons}
    </div>
  )

  if (isCardPayment) {
    footer = (
      <CardReader
        uuid={uuid}
        total={grandTotal.replace('$', '')}
        onCancel={onClose}
        onSuccess={handleOnSuccess}
        onDone={onDone}
        authorizationType={'auth'}
        peripheralStatus={peripheralStatus}
        orderNumber={nextPosOrderNumber(true)}
      />
    )
  }

  const handlePrint = () => {
    if (!deviceReceiptPrinterUuid) {
      setShowPrinterSelection(!showPrinterSelection)
    } else {
      printOrder({
        printType: 'suite-receipt-auth',
        printerUuids: [deviceReceiptPrinterUuid],
        orderUuid: uuid,
      })
    }
  }

  const printerSelection = (
    <RadioSelectionModal
      show={showPrinterSelection}
      toggleShow={setShowPrinterSelection}
      items={printers}
      title={"Print Receipt"}
      selectionTitle={"Select a printer"}
      buttonText={"PRINT"}
      className={classNames}
      onDone={(printerSelected) =>
        printOrder({
          printType: "suite-receipt-auth",
          printerUuids: [printerSelected?.uuid],
          orderUuid: uuid,
        })
      }
    />
  )

  if (nonCashPaymentSelected && (signatureRequired || !isNoTipMenu)) {
    footer = (
      <div className={styles.buttonContainer}>
        <Button disabled={loading} key="Finalize Later" onClick={onDone} className={styles.actionButton}>Finalize Later</Button>
        <Button disabled={loading || unsyncedOrder} key="Print Slip" onClick={handlePrint} className={styles.actionButton}>Print Slip</Button>
        <Button disabled={loading || unsyncedOrder} key="View Check" onClick={() => onViewCheck(uuid)} className={[styles.actionButton, styles.mintGreenHighlight]}>Review & Sign Screen</Button>
      </div>
    )
  }

  // If ordering preferences or totals are changing, then the system hasn't reached a default
  // exemption consensus, so the exemptions should be overwritten with what is dictated by the
  // totals and preferences.
  useDeepCompareEffect(() => {
    const defaultExemptionOverrides = exemptionOverrides ?? makeDefaultExemptions(remoteOrderTotal, orderingPreference)
    updateExemptionsAndTotals(defaultExemptionOverrides)
  }, [remoteOrderTotal, orderingPreference])

  let serviceChargeDisplay = remoteOrderTotal?.serviceChargeDisplay
  serviceChargeDisplay = remoteOrderTotal?.serviceChargeInCents + Math.abs(remoteOrderTotal?.serviceChargeDiscountInCents)
  serviceChargeDisplay = centsToDollars(serviceChargeDisplay)
  let discountDisplay = remoteOrderTotal?.discountDisplay ?? '-'


  if (!exemptions.applyServiceFee) {
    serviceChargeDisplay = undefined

    if (remoteOrderTotal?.serviceChargeDiscountInCents) {
      const calculatedDiscount = remoteOrderTotal?.discountAmountInCents - remoteOrderTotal?.serviceChargeDiscountInCents
      discountDisplay = centsToDollars(calculatedDiscount)
    }
  }
  
  return (
    <div>
      <SidebarModal title="Pay Now" subTitle={displayName} show leftAccessory={backButton} onClose={cardPaymentCompleted ? onDone : onClose} {...props} className={classNames}>
        <div className={styles.bodyContainer}>
          { (!frozen && isSuitesMode) && <List
            items={discounts}
            component={ExemptableItem}
            selectionType="multiple"
            defaultSelectedIndexes={defaultSelectedDiscountIndexes}
            selectionDidChange={handleDiscountsDidChange}
            className={styles.discounts}
            containerType="ul"
            beforeChange={(item, index, proceed) => {
              if (requiresApproval(item, index)) {
                requestApproval(() => {
                  proceed()
                })
              } else {
                proceed()
              }
            }}
          /> }
          <TabOrderTotals
            className={styles.tabOrderTotals}
            discount={discountDisplay ?? '-'}
            exemptions={exemptions}
            itemCount={itemCount}
            nonExemptableTaxRules={values(remoteOrderTotal?.nonExemptableTaxRules ?? [])}
            serviceCharge={serviceChargeDisplay}
            serviceChargeName={remoteOrderTotal?.serviceChargeName}
            subtotal={subtotal}
            tax={remoteOrderTotal?.taxDisplay ?? '-'}
            total={grandTotal}
          />
          <VNNotesField ref={notesRef}/>
          {footer}
          {approvalModal}
          { showPrinterSelection && printerSelection }
        </div>
      </SidebarModal>
    </div>
  )
}

TabOrderPayNowModal.defaultProps = {
  payment: 'Card On File',
  items: [],
  subtotal: '',
  total: '',
  itemCount: 0,
  isNewOrder: true,
  orderId: '',
  remoteOrderTotal: {},
  orderTotalFromOrder: {},
  exemptionOverrides: undefined,
  pendingMutation: undefined,
  onClose: () => {},
  onSuccess: () => {},
  onDone: () => {},
  onCashPayment: () => {},
  onCloseWithStoredCard: () => {},
  onCloseWithNewCard: () => {},
  getRemoteOrderTotal: () => {},
}

TabOrderPayNowModal.propTypes = {
  payment: PropTypes.string,
  items: PropTypes.array,
  subtotal: PropTypes.string,
  total: PropTypes.string,
  itemCount: PropTypes.number,
  isNewOrder: PropTypes.bool,
  pendingMutation: PropTypes.string,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  onDone: PropTypes.func,
  onCashPayment: PropTypes.func,
  onCloseWithStoredCard: PropTypes.func,
  onCloseWithNewCard: PropTypes.func,
  getRemoteOrderTotal: PropTypes.func,
  orderId: PropTypes.string,
  remoteOrderTotal: PropTypes.shape({
    discountDisplay: PropTypes.string,
    serviceChargeDisplay: PropTypes.string,
    taxDisplay: PropTypes.string,
    totalDisplay: PropTypes.string,
  })
}

export default TabOrderPayNowModal
